import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./RunningStage.css";

const PLAYER_WIDTH = 64;
const PLAYER_HEIGHT = 64;
const TRACK_BG = "/unhealthy-game/images/track.png";
const OBSTACLE_TYPES = [
  { className: "running-stage__obstacle--one", width: 70, height: 40 },
  { className: "running-stage__obstacle--two", width: 100, height: 70 },
  { className: "running-stage__obstacle--three", width: 70, height: 40 },
  { className: "running-stage__obstacle--four", width: 70, height: 40 },
];
const PLAYER_BG = "/unhealthy-game/images/men.gif";
const OBSTACLE_BG = {
  "running-stage__obstacle--one": "/unhealthy-game/images/obj01.png",
  "running-stage__obstacle--two": "/unhealthy-game/images/obj02.png",
  "running-stage__obstacle--three": "/unhealthy-game/images/obj03.png",
  "running-stage__obstacle--four": "/unhealthy-game/images/obj04.png",
};

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

function collides(a, b) {
  return !(
    a.x + a.width < b.x ||
    a.x > b.x + b.width ||
    a.y + a.height < b.y ||
    a.y > b.y + b.height
  );
}

function getRoadBounds(width, height) {
  return {
    left: width * 0.22,
    right: width * 0.78,
    top: height * 0.04,
    bottom: height * 0.98,
  };
}

export default function RunningStage({ data, onDone }) {
  const durationSeconds = data.durationSeconds || 65;
  const [started, setStarted] = useState(false);
  const [running, setRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(durationSeconds);
  const [player, setPlayer] = useState({ x: 130, y: 360, width: PLAYER_WIDTH, height: PLAYER_HEIGHT });
  const [obstacles, setObstacles] = useState([]);
  const [lines, setLines] = useState([]);
  const arenaRef = useRef(null);
  const keysRef = useRef({ ArrowUp: false, ArrowDown: false, ArrowLeft: false, ArrowRight: false });
  const playerRef = useRef(player);
  const obstaclesRef = useRef([]);
  const linesRef = useRef([]);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const doneRef = useRef(false);
  const speed = 6;

  const lineItems = useMemo(
    () =>
      lines.map((line, index) => (
        <div key={`line-${index}`} className="running-stage__line" style={{ top: `${line.y}px` }} />
      )),
    [lines]
  );

  const obstacleItems = useMemo(
    () =>
      obstacles.map((obs, index) => (
        <div
          key={obs.id}
          className={`running-stage__obstacle ${obs.className}`}
          style={{
            width: `${obs.width}px`,
            height: `${obs.height}px`,
            top: `${obs.y}px`,
            left: `${obs.x}px`,
            zIndex: 3 + index,
            backgroundImage: `url(${OBSTACLE_BG[obs.className]})`,
          }}
        />
      )),
    [obstacles]
  );

  const stopAll = useCallback(() => {
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRunning(false);
  }, []);

  const finish = useCallback(
    (points) => {
      if (doneRef.current) return;
      doneRef.current = true;
      stopAll();
      onDone(points);
    },
    [onDone, stopAll]
  );

  const createInitialState = useCallback(() => {
    const arena = arenaRef.current;
    const width = arena?.clientWidth || 720;
    const height = arena?.clientHeight || 520;
    const road = getRoadBounds(width, height);
    const nextPlayer = {
      x: Math.max(road.left, Math.min(road.right - PLAYER_WIDTH, width * 0.48)),
      y: Math.max(road.top, Math.min(road.bottom - PLAYER_HEIGHT, road.bottom - PLAYER_HEIGHT - 14)),
      width: PLAYER_WIDTH,
      height: PLAYER_HEIGHT,
    };
    const nextObstacles = OBSTACLE_TYPES.map((type, index) => ({
      id: `obs-${index}`,
      className: type.className,
      width: type.width,
      height: type.height,
      x: randomBetween(road.left, Math.max(road.left + 1, road.right - type.width)),
      y: -(index + 1) * randomBetween(220, 360),
    }));
    const nextLines = Array.from({ length: 5 }, (_, index) => ({ y: index * 150 - 120 }));

    playerRef.current = nextPlayer;
    obstaclesRef.current = nextObstacles;
    linesRef.current = nextLines;
    setPlayer(nextPlayer);
    setObstacles(nextObstacles);
    setLines(nextLines);
    setTimeLeft(durationSeconds);
  }, [durationSeconds]);

  const gameLoop = useCallback(() => {
    const arena = arenaRef.current;
    if (!arena) return;
    const width = arena.clientWidth;
    const height = arena.clientHeight;
    const road = getRoadBounds(width, height);
    const nextPlayer = { ...playerRef.current };

    if (keysRef.current.ArrowUp) nextPlayer.y -= speed;
    if (keysRef.current.ArrowDown) nextPlayer.y += speed;
    if (keysRef.current.ArrowLeft) nextPlayer.x -= speed;
    if (keysRef.current.ArrowRight) nextPlayer.x += speed;

    nextPlayer.x = Math.max(road.left, Math.min(road.right - nextPlayer.width, nextPlayer.x));
    nextPlayer.y = Math.max(road.top, Math.min(road.bottom - nextPlayer.height, nextPlayer.y));

    const nextLines = linesRef.current.map((line) => {
      const y = line.y + speed;
      return { y: y >= height ? -120 : y };
    });

    const nextObstacles = obstaclesRef.current.map((obs) => {
      let y = obs.y + speed;
      let x = obs.x;
      if (y >= height + 40) {
        y = -randomBetween(180, 540);
        x = randomBetween(road.left, Math.max(road.left + 1, road.right - obs.width));
      }
      return { ...obs, x, y };
    });

    playerRef.current = nextPlayer;
    obstaclesRef.current = nextObstacles;
    linesRef.current = nextLines;
    setPlayer(nextPlayer);
    setObstacles(nextObstacles);
    setLines(nextLines);

    const hit = nextObstacles.some((obs) =>
      collides(nextPlayer, { x: obs.x, y: obs.y, width: obs.width, height: obs.height })
    );
    if (hit) {
      finish(5);
      return;
    }

    rafRef.current = window.requestAnimationFrame(gameLoop);
  }, [finish]);

  const startGame = useCallback(() => {
    doneRef.current = false;
    setStarted(true);
    setRunning(true);
    createInitialState();
  }, [createInitialState]);

  useEffect(() => {
    if (!running) return undefined;
    rafRef.current = window.requestAnimationFrame(gameLoop);
    timerRef.current = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          finish(10);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [running, gameLoop, finish]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!running) return;
      if (event.key in keysRef.current) {
        event.preventDefault();
        keysRef.current[event.key] = true;
      }
    };
    const onKeyUp = (event) => {
      if (!running) return;
      if (event.key in keysRef.current) {
        event.preventDefault();
        keysRef.current[event.key] = false;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [running]);

  useEffect(
    () => () => {
      stopAll();
    },
    [stopAll]
  );

  const nudge = (dx, dy) => {
    if (!running || !arenaRef.current) return;
    const width = arenaRef.current.clientWidth;
    const height = arenaRef.current.clientHeight;
    const road = getRoadBounds(width, height);
    const nextPlayer = {
      ...playerRef.current,
      x: Math.max(road.left, Math.min(road.right - PLAYER_WIDTH, playerRef.current.x + dx)),
      y: Math.max(road.top, Math.min(road.bottom - PLAYER_HEIGHT, playerRef.current.y + dy)),
    };
    playerRef.current = nextPlayer;
    setPlayer(nextPlayer);
  };

  return (
    <div className="running-stage">
      <div className="running-stage__track-shell">
        <div className="running-stage__track-shell-inner" style={{ backgroundImage: `url(${TRACK_BG})` }}>
          <div className="running-stage__track" ref={arenaRef}>
            {lineItems}
            {obstacleItems}
            {started ? (
              <div
                className="running-stage__player"
                style={{
                  top: `${player.y}px`,
                  left: `${player.x}px`,
                  width: `${player.width}px`,
                  height: `${player.height}px`,
                  backgroundImage: `url(${PLAYER_BG})`,
                }}
              />
            ) : null}
            {!started ? (
              <button type="button" className="running-stage__start" onClick={startGame}>
                <div className="running-stage__start-card">
                  <p>You want to prepare for the upcoming marathon.</p>
                  <p>Weaker has placed obstacles on the running track.</p>
                  <p>Use arrow keys to move and avoid collisions.</p>
                  <p className="running-stage__start-link">Click here to start the game</p>
                </div>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="running-stage__status">Track Timer: {Math.max(0, timeLeft)}s</div>

      <div className="running-stage__controls">
        <button type="button" onClick={() => nudge(0, -34)}>
          Up
        </button>
        <button type="button" onClick={() => nudge(-42, 0)}>
          Left
        </button>
        <button type="button" onClick={() => nudge(42, 0)}>
          Right
        </button>
        <button type="button" onClick={() => nudge(0, 34)}>
          Down
        </button>
      </div>
    </div>
  );
}
